/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import memo from "memoize-one";
import { createSelector } from "reselect";
import { RootState } from "..";
import { FilterKeys } from "@/store/state";
import {
  getFilteredResourceByKey,
  machineMatchesLocationOrMachineFilter,
  selectAllLocations,
  selectMachineTreeNodes,
  type MachineTreeNode,
} from "../selectors";
import constants from "@/constants";
import {
  LocationRequiredProperties,
  getActiveLocations,
} from "@/presentation/Location";
import { arraysMatch } from "@/modules/string";
import { initialFilterState } from "../reducer";

/** Creates a memoized filter function.
 * If the filter strings include "Unknown", includes results from `resource` where `key` is falsy */
export function getFilteredResourceByKeyMapUndefinedToUnknown(key: string) {
  return memo(function <T extends { [key: string]: any }>(
    resource: T[],
    filters: string[]
  ): T[] {
    if (!filters || !filters.length) return resource;
    return resource.filter((r) => {
      return filters.some((filter) =>
        !r[key] && filter === "Unknown" ? true : r[key] === filter
      );
    });
  }) as <T>(resource: T[], filters: string[]) => T[];
}

type FilterSelectionType =
  /** This and all descendants are not selected */
  | "none"
  /** This and all descendants are selected */
  | "all"
  /** This is not selected; a descendant is selected */
  | "inner"
  /** This is selected; a descendant is not selected */
  | "outer";

const getLocationSelectionTypes = (
  locations: LocationRequiredProperties[],
  activeLocations: string[]
): Mappy<FilterSelectionType> => {
  if (!activeLocations?.length) return {};
  return locations
    .map((l) => {
      return {
        [l.id]: (activeLocations.includes(l.id)
          ? l.descendants.every((d) => activeLocations.includes(d))
            ? "all"
            : "outer"
          : l.descendants.some((d) => activeLocations.includes(d))
            ? "inner"
            : "none") as FilterSelectionType,
      };
    })
    .reduce((a, b) => {
      return { ...a, ...b };
    }, {});
};

const selectActiveFilters = (s: RootState) => s.filter.active;
export const selectDraftFilters = (s: RootState) => s.filter.draft;

/** Filtered location list */
export const selectLocations = createSelector(
  [selectAllLocations, selectActiveFilters],
  (locations, f) => getFilteredResourceByKey("id")(locations, f.locationId)
);

/**
 * Active locations, with all descendants active, for the location filter component (draft)
 */
export const selectActiveLocations = createSelector(
  [selectAllLocations, selectDraftFilters],
  (locations, f) => getActiveLocations(locations, f.locationId)
);

/**
 * Selection type (none, all, inner, or outer) for locations in the location filter component (draft)
 */
export const selectLocationSelectionTypes = createSelector(
  [selectAllLocations, selectDraftFilters],
  (locations, f) => getLocationSelectionTypes(locations, f.locationId)
);

/** Filtered machine list */
export const selectMachines = createSelector(
  [
    (s: RootState) => s.global.machines,
    selectActiveFilters,
    selectAllLocations,
  ],
  (gm, f, locations) =>
    gm
      .filter((m) => machineMatchesLocationOrMachineFilter(m, f, locations))
      .filter((m) => !f.machineType.length || f.machineType.includes(m.type))
);

/** Recursively collects all descendant node ids (locations and machines) beneath a Machine tree node */
export function collectMachineTreeDescendantIds(
  node: MachineTreeNode
): string[] {
  if (!node.children?.length) return [];
  return node.children.flatMap((child) => [
    child.id,
    ...collectMachineTreeDescendantIds(child as MachineTreeNode),
  ]);
}

/** Selection type (none/all/inner/outer) for every node in the Machine filter tree (draft).
 * Computed against the full, unfiltered-by-search tree so a node reads "all" whenever it (or an
 * ancestor location) is directly selected, OR every one of its children is itself fully
 * selected - covering both the normal collapsed-to-a-parent-id state and any transient state
 * where descendants happen to be individually selected without their parent id being stored. */
export const selectMachineTreeSelectionTypes = createSelector(
  [selectMachineTreeNodes, selectDraftFilters],
  (nodes, f) => {
    const activeIds = new Set([...f.locationId, ...f.machineId]);
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const resolved = new Map<string, boolean>();
    const visiting = new Set<string>();

    function isSelected(node: MachineTreeNode): boolean {
      const cached = resolved.get(node.id);
      if (cached !== undefined) return cached;
      if (node.id === constants.unassignedMachinesLocation.name) return false;
      if (visiting.has(node.id)) return false; // break ancestor/descendant cycles conservatively
      visiting.add(node.id);
      let result = activeIds.has(node.id);
      // Check ancestor coverage before descending into children, so a child asking about its own
      // ancestor coverage (via its own parent check) sees an already-resolved parent rather than
      // triggering the cycle guard.
      if (!result && node.parentId != null) {
        const parent = nodeMap.get(node.parentId);
        if (parent) result = isSelected(parent);
      }
      if (!result && node.children?.length) {
        result = node.children.every(isSelected);
      }
      visiting.delete(node.id);
      resolved.set(node.id, result);
      return result;
    }

    const types: Mappy<FilterSelectionType> = {};
    nodes.forEach((node) => {
      const descendants = collectMachineTreeDescendantIds(node);
      if (node.id === constants.unassignedMachinesLocation.name) {
        const machineDescendants = descendants.filter(
          (id) => nodeMap.get(id)?.isMachine
        );
        const allMachineDescendantsSelected =
          machineDescendants.length > 0 &&
          machineDescendants.every((d) => activeIds.has(d));
        const someMachineDescendantsSelected = machineDescendants.some((d) =>
          activeIds.has(d)
        );
        types[node.id] = allMachineDescendantsSelected
          ? "all"
          : someMachineDescendantsSelected
            ? "inner"
            : "none";
        return;
      }
      if (isSelected(node)) {
        types[node.id] = "all";
        return;
      }
      if (!descendants.length) {
        types[node.id] = "none";
        return;
      }
      const someDescendantSelected = descendants.some((d) => {
        const descendantNode = nodeMap.get(d);
        return !!descendantNode && isSelected(descendantNode);
      });
      types[node.id] = someDescendantSelected ? "inner" : "none";
    });
    return types;
  }
);

/** Node ids (locations and machines) that are fully selected, for the Machine filter tree (draft) */
export const selectActiveMachineTreeIds = createSelector(
  [selectMachineTreeSelectionTypes],
  (types) => Object.keys(types).filter((id) => types[id] === "all")
);

/** Is the filtered machine list shorter than the global list? */
export const selectAreMachinesFiltered = createSelector(
  [(s: RootState) => s.global.machines, selectMachines],
  (gm, sm) => gm.length > sm.length
);

/** Does the filter have a draft state different from the active state? */
export const selectIsFilterModified = createSelector(
  [(s: RootState) => s.filter.active, (s: RootState) => s.filter.draft],
  (a, b) => {
    const filterkeys = constants.filterKeys;
    return filterkeys.some((key) => !arraysMatch(a[key], b[key]));
  }
);

/** Does the filter have a draft state different from its initial state? */
export const selectIsFilterCleared = createSelector(
  [
    (s: RootState, key: FilterKeys) => s.filter.draft[key],
    (_, key: FilterKeys) => initialFilterState()[key],
  ],
  arraysMatch
);

// Use this to disable/enable UI elements based on whether filters are cleared
/** Are all draft filter values the same as initial filter values? */
export const selectIsAllFilterDraftInitial = createSelector(
  [(s: RootState) => s.filter.draft],
  (draftFilter) => {
    const filterkeys = constants.filterKeys;
    const initialFilter = initialFilterState();
    return filterkeys.every((key) =>
      arraysMatch(draftFilter[key], initialFilter[key])
    );
  }
);

// Memoized selectors to prevent unnecessary rerenders
export const selectLocationIds = createSelector(
  [(state: RootState) => state.global.locations],
  (locations) => locations.map((m) => m.id)
);

export const selectJobNames = createSelector(
  [(state: RootState) => state.jobPerformance.jobTypes],
  (jobTypes) => jobTypes.map((j) => j.name)
);
